export type AbTestChoice = {
  choice: string;
  weight: number;
  /** A human readable description of what the player sees in this group, shown in the admin and AB test tools */
  description: string;
  mobileOnly?: boolean;
};

/** All known AB Tests. Other parts of the code need to know ALL of the choices for an AB test, so that it can
 * generate the key used for local storage.
 *
 * Choices may be assigned on the client side or the server side. Some are saved into the abtest_variant column.
 */
export const allAbTests = {
  // Test names and values should be deleted from here when the test finishes

  // AB test groups where choices are assigned on the client side and may be saved into the abtest_variant column through an API call
  EnterFlowProgress: [
    { choice: 'CONTROL', weight: 1, description: 'Original enter flow header' },
    {
      choice: 'VARIANT1',
      weight: 1,
      description:
        'Static, centred header with progress through the enter flow',
    },
    {
      choice: 'VARIANT2',
      weight: 0,
      description: 'No longer used',
    },
    {
      choice: 'VARIANT3',
      weight: 0,
      description: 'No longer used',
    },
  ],
  ChooseYourPlanBoost: [
    {
      choice: 'CONTROL',
      weight: 1,
      description: 'Hero image, optional boost, premium then lite tiles',
    },
    {
      choice: 'V5',
      weight: 1,
      description: 'Hero image, optional boost, lite then premium tiles',
    },
    {
      choice: 'ORIGINALV1',
      weight: 1,
      description: 'No hero image, optional boost (min £1.50/£2.95)',
    },
    {
      choice: 'ORIGINALV2',
      weight: 1,
      description: 'No hero image, boosted (min £2/£3.45)',
    },
  ],
  HomePage2026: [
    {
      choice: 'CONTROL',
      weight: 1,
      description: 'Original home page and footer',
    },
    {
      choice: 'V1',
      weight: 0,
      description: '2026 home page V1 with the 2026 footer and no draw countdown (no longer used)',
    },
    {
      choice: 'V4',
      weight: 1,
      description: 'Latest 2026 home page variant with the 2026 footer and draw countdown',
    },
  ],
  SkipChooseYourPlan2026: [
    {
      choice: 'CONTROL',
      weight: 1,
      description: 'Players see the choose your plan page',
    },
    {
      choice: 'TEST',
      weight: 0,
      description: 'Players skip the choose your plan page',
    },
  ],
  Checkout2026: [
    {
      choice: 'CONTROL',
      weight: 1,
      description: 'Control',
    },
    {
      choice: 'V1',
      weight: 0,
      description: '2026 checkout V1 (Lighter variant)',
    },
    {
      choice: 'V2',
      weight: 0,
      description: '2026 checkout V2 (Darker variant)',
    },
  ],

  // AB test groups where choices are assigned on the server side
  MonthlyGiveaway: [
    { choice: 'CONTROL', weight: 0, description: 'No monthly giveaway' },
    {
      choice: 'YES',
      weight: 1,
      description: 'Entered into the monthly giveaway',
    },
  ],
  ConsolationPrize2: [
    { choice: 'CONTROL', weight: 2, description: 'No consolation prize' },
    {
      choice: 'YES',
      weight: 4,
      description: 'Consolation prize when a paid entry wins nothing',
    },
    {
      choice: 'YES_MIN_5',
      weight: 4,
      description:
        'Consolation prize when a paid entry wins nothing, subject to a minimum spend',
    },
  ],
  TreatWinEmails: [
    { choice: 'CONTROL', weight: 1, description: 'Original treat win emails' },
    { choice: 'TEST', weight: 1, description: 'V2 treat win email templates' },
  ],
} as const satisfies Record<string, Array<AbTestChoice>>;

/** A list of the AB tests that must be synced between frontend and backend */
export const serverSideAndClientSideTestNames: (keyof typeof allAbTests)[] = [
  // Test names should be deleted from here when the test finishes
  'ChooseYourPlanBoost',
];

/** Returns the description of an AB test choice, or undefined when the test or choice is no longer in allAbTests */
export function getAbTestChoiceDescription(
  testName: string,
  choice: string,
): string | undefined {
  const choices: readonly AbTestChoice[] | undefined =
    allAbTests[testName as keyof typeof allAbTests];
  return choices?.find((x) => x.choice === choice)?.description;
}
